import { api } from "./client";
import type { FinalVerdict, ExaminationDetail } from "./examinations";

export type FormTemplate = "form_086" | "form_083" | "form_089" | "custom";

export interface ContraindicationItem {
  label: string;
  severity: string;
  rule_id: string;
}

export interface VerdictPreflight {
  can_issue: boolean;
  missing_sections: string[];
  pending_lab_orders: number;
  suggested_contraindications: ContraindicationItem[];
}

export interface FormRead {
  id: string;
  examination_id: string;
  template: FormTemplate;
  version: number;
  form_number: string;
  qr_token: string;
  pdf_sha256: string;
  signed_by_user_id: string;
  generated_at: string;
  revoked_at: string | null;
  revoke_reason: string | null;
  printed_count: number;
}

export interface FormGenerateResponse {
  form: FormRead;
  download_url: string;
}

export async function getPreflight(examId: string): Promise<VerdictPreflight> {
  const { data } = await api.get<VerdictPreflight>(
    `/examinations/${examId}/verdict-preflight`,
  );
  return data;
}

export interface VerdictIssuePayload {
  verdict: FinalVerdict;
  notes: string;
  valid_until?: string | null;
  diagnosis_summary?: string | null;
  profession_fitness?: "fit" | "unfit" | null;
  pregnancy_relevant?: boolean | null;
  referral_kind?: "stationary" | "outpatient" | "sanatorium" | "sankurort" | "none" | null;
  referral_notes?: string | null;
  final_decision?: "fit" | "unfit" | null;
}

export async function issueVerdict(
  examId: string,
  payload: VerdictIssuePayload,
): Promise<VerdictPreflight> {
  const { data } = await api.post<VerdictPreflight>(
    `/examinations/${examId}/verdict`,
    payload,
  );
  return data;
}

export async function generateForm(
  examId: string,
  template: FormTemplate,
  lang?: string,
): Promise<FormGenerateResponse> {
  const { data } = await api.post<FormGenerateResponse>(
    `/examinations/${examId}/forms`,
    { template },
    { params: lang ? { lang } : undefined },
  );
  return data;
}

export function downloadFormPdfUrl(formId: string): string {
  return `/api/v1/forms/${formId}/pdf`;
}
